import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { formatIDR, parseIDR, parsePrice, formatDate, formatQty, parseQty, todayISO } from "@/lib/format";
import { Card, PageHeader, Button, Field, Input, Select, Badge, Textarea, Modal } from "@/components/ui-kit";
import FilePreview from "@/components/FilePreview";
import MasterCombo from "@/components/MasterCombo";
import { SjPickerModal } from "@/components/billing/SjPickerModal";
import { ManualSjModal } from "@/components/billing/ManualSjModal";
import { AutocompleteInput } from "@/components/AutocompleteInput";
import { recordHistoryMany } from "@/lib/history";
import { CheckCircle2, Circle, Printer, Upload, FileText, Eye, Plus, Pencil, Trash2, ArrowDownAZ, CalendarArrowDown, Download } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";

const STAGES = ["config_stage", "items_stage", "preview_stage", "confirm_stage"];
const RECAP_TITLES = ["Rekap Pengiriman", "Rekap Penggunaan Jasa"];

export default function Billing() {
  const { t } = useLang();
  const location = useLocation();
  const editId = new URLSearchParams(location.search).get("edit");
  const [stage, setStage] = useState(0);
  const [editingId, setEditingId] = useState(editId || null);
  const [pos, setPos] = useState([]);
  const [sjs, setSjs] = useState([]);
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [banks, setBanks] = useState([]);
  const [signatories, setSignatories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clientManual, setClientManual] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [fakturUploading, setFakturUploading] = useState(false);
  const BLANK_CFG = {
    po_id: "",
    show_sj_no: false,
    show_vehicle_no: false,
    show_destination: false,
    use_vat: true,
    vat_rate: 12,
    use_dpp_nilai_lain: true,
    use_discount: false,
    discount_amount: 0,
    use_pph: false,
    pph_rate: 2,
    use_dp: false,
    dp_amount: 0,
    dp_vat_amount: 0,
    // Iter v34{ad} — DP mode: "amount" (default) or "percentage" (of subtotal-discount).
    dp_mode: "amount",
    dp_percentage: 0,
    // Iter v34{ad} — Standalone Down Payment invoice mode.
    is_dp_invoice: false,
    dp_invoice_amount: 0,     // Nominal for the DP line item
    // Iter v34{af} — DP invoice can also be percentage-based (of PO total).
    dp_invoice_mode: "amount", // "amount" | "percentage"
    dp_invoice_percentage: 0,
    // Iter v34bn — Invoice tanpa SJ: buat invoice langsung dari PO, skip SJ picker.
    bill_direct_from_po: false,
    client_id: "",
    client_name: "",
    category: "delivery",
    invoice_number: "",
    invoice_date: todayISO(),
    due_days: 14,
    due_date: "",  // Item 3 iter v19 — optional manual override
    recap_title: RECAP_TITLES[0],
    company_id: "",
    bank_account_id: "",
    signatory: "",
    invoice_note: "Pembayaran jatuh tempo 14 hari",
    receipt_description: "Pelunasan tagihan",
    faktur_file_path: "",
    faktur_file_id: "",
    faktur_file_name: "",
    faktur_file_content_type: "",
    notes_header: "",
    manual_sjs: [],
  };
  const [cfg, setCfg] = useState(BLANK_CFG);
  const [items, setItems] = useState([]);
  const [selectedSj, setSelectedSj] = useState([]);
  const [notes, setNotes] = useState("");
  const [createdInvoice, setCreatedInvoice] = useState(null);
  const [prices, setPrices] = useState([]);
  const [sjCodes, setSjCodes] = useState([]);
  // SJ picker / manual entry state
  const [sjPickerOpen, setSjPickerOpen] = useState(false);
  const [sjPickerSelection, setSjPickerSelection] = useState([]);
  const [sjPickerFilterPo, setSjPickerFilterPo] = useState(true);
  const blankManual = { sj_number: "", date: todayISO(), vehicle_no: "", delivery_location: "", items: [{ material_name: "", unit: "", quantity: 1 }] };
  const [manualOpen, setManualOpen] = useState(false);
  const [manualEditIdx, setManualEditIdx] = useState(null);
  const [manualForm, setManualForm] = useState(blankManual);
  // Iter v34cf — Sequential form: Klien → Tanggal → PO. Sisa field disabled sampai 3 langkah selesai.
  const [poTouched, setPoTouched] = useState(false);

  useEffect(() => {
    api.get("/pos").then((r) => setPos(r.data));
    // Iter 64 (#3) — read global PPN default & apply to fresh invoice config.
    api.get("/settings").then((r) => {
      const ppnOn = r.data?.default_vat_enabled !== false;
      const ppnRate = r.data?.default_vat_rate ?? 12;
      setCfg((c) => ({ ...c, use_vat: ppnOn, vat_rate: ppnRate }));
    }).catch(() => {});
    // Only show SJs that haven't reached their billing_count_allowed (Iter15 #4)
    // — once a SJ exhausts its allowed count, it disappears from the picker
    // (unless its invoice gets deleted, which re-opens it).
    api.get("/surat-jalan", { params: { unbilled_only: true } }).then((r) => setSjs(r.data));
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/companies").then((r) => setCompanies(r.data));
    api.get("/banks").then((r) => setBanks(r.data));
    api.get("/signatories").then((r) => setSignatories(r.data));
    api.get("/masters/categories").then((r) => setCategories(r.data));
    api.get("/masters/sj_codes").then((r) => setSjCodes(r.data)).catch(() => {});
    api.get("/prices").then((r) => setPrices(r.data)).catch(() => {});
    if (editId) {
      api.get(`/invoices/${editId}`).then(async (r) => {
        const inv = r.data;
        setCfg({
          ...BLANK_CFG,
          ...inv,
          // Iter v34cz — Invoice DP menyimpan dp_amount/dp_mode/dp_percentage; peta balik
          // ke field wizard (dp_invoice_*) agar bisa diedit tanpa mengetik ulang nominal.
          ...(inv.is_dp_invoice ? {
            dp_invoice_amount: inv.dp_amount || 0,
            dp_invoice_mode: inv.dp_mode === "percentage" ? "percentage" : "amount",
            dp_invoice_percentage: inv.dp_percentage || 0,
          } : {}),
        });
        setItems(inv.items || []);
        setSelectedSj(inv.sj_ids || []);
        setNotes(inv.notes || "");
        setEditingId(inv.id);
        // When editing, include SJs attached to this invoice even if they'd
        // otherwise be filtered out by unbilled_only.
        if (inv.sj_ids && inv.sj_ids.length) {
          try {
            const extras = await Promise.all(inv.sj_ids.map((sid) => api.get(`/surat-jalan/${sid}`).then((x) => x.data).catch(() => null)));
            setSjs((prev) => {
              const seen = new Set(prev.map((s) => s.id));
              const merged = [...prev];
              for (const x of extras) { if (x && !seen.has(x.id)) merged.push(x); }
              return merged;
            });
          } catch { /* ignore */ }
        }
      }).catch(() => toast.error("Invoice tidak ditemukan"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // when PO chosen, prefill client + notes header (items now derived from SJs in Step 3)
  useEffect(() => {
    if (!cfg.po_id) return;
    const po = pos.find((p) => p.id === cfg.po_id);
    if (po) {
      // Auto-fill client (look up by name in client list)
      const cl = clients.find((c) => c.name === po.client_name);
      setCfg((c) => ({
        ...c,
        client_id: cl?.id || "",
        client_name: po.client_name || c.client_name,
        category: po.category || c.category,
        notes_header: c.notes_header || (po.notes || ""),
      }));
    }
  }, [cfg.po_id, pos, clients]);

  // Iter v34{ae} — DP auto-link: when PO chosen (main invoice), prefill Uang Muka
  // with any Outstanding DP paid on prior DP invoices for the same PO.
  const [dpOutstanding, setDpOutstanding] = useState(null);
  useEffect(() => {
    if (!cfg.po_id || cfg.is_dp_invoice) { setDpOutstanding(null); return; }
    api.get(`/invoices/dp-summary?po_id=${encodeURIComponent(cfg.po_id)}${editingId ? `&exclude_invoice_id=${encodeURIComponent(editingId)}` : ""}`)
      .then((r) => {
        const data = r.data || {};
        setDpOutstanding(data);
        const remaining = Number(data.outstanding_dp || 0);
        if (remaining > 0 && !editingId) {
          setCfg((c) => (c.use_dp ? c : {
            ...c,
            use_dp: true,
            dp_mode: "amount",
            dp_amount: remaining,
            dp_vat_amount: Number(data.outstanding_dp_vat || 0),
          }));
        }
      })
      .catch(() => setDpOutstanding(null));
  }, [cfg.po_id, cfg.is_dp_invoice, editingId]);

  // Auto-update invoice number preview when VAT/company changes
  useEffect(() => {
    if (cfg.invoice_number) return; // user typed manually
    const params = new URLSearchParams({ use_vat: String(cfg.use_vat), company_id: cfg.company_id || "" }).toString();
    api.get(`/invoices/next-number?${params}`)
      .then((r) => setCfg((c) => ({ ...c, _suggested_number: r.data.invoice_number })))
      .catch(() => {});
  }, [cfg.use_vat, cfg.company_id, cfg.invoice_number]);

  // Auto-suggest leading number from last invoice in same category (#3)
  useEffect(() => {
    if (cfg.invoice_number) return;
    api.get(`/next-invoice-prefix?category=${encodeURIComponent(cfg.category || "")}`)
      .then((r) => setCfg((c) => {
        if (c.invoice_number) return c;
        const lead = r.data.next || "";
        // Build invoice_number by replacing/prepending the leading number on the suggested template
        const sug = c._suggested_number || "";
        if (lead && sug) {
          // Replace the leading digit run with the new lead
          const rebuilt = sug.replace(/^\d+/, lead);
          return { ...c, _suggested_lead: lead, _suggested_number: rebuilt };
        }
        return { ...c, _suggested_lead: lead };
      }))
      .catch(() => {});
  }, [cfg.category, cfg._suggested_number, cfg.invoice_number]);

  const filteredSj = sjs.filter((s) => s.po_id === cfg.po_id); // eslint-disable-line no-unused-vars

  // --- SJ Wizard helpers (Step 2) ---------------------------------------
  const combinedSjs = useMemo(() => {
    const fromDb = selectedSj
      .map((id) => sjs.find((x) => x.id === id))
      .filter(Boolean)
      .map((s) => ({ kind: "db", key: s.id, ...s }));
    const fromManual = (cfg.manual_sjs || []).map((m, idx) => ({ kind: "manual", key: `m-${idx}`, idx, ...m }));
    return [...fromDb, ...fromManual];
  }, [selectedSj, sjs, cfg.manual_sjs]);

  const sjPickerSource = (() => {
    const base = sjs.filter((s) => !selectedSj.includes(s.id));
    if (cfg.po_id && sjPickerFilterPo) {
      // Primary filter: by PO
      return base.filter((s) => s.po_id === cfg.po_id);
    }
    if (cfg.client_name) {
      // Secondary filter: by Klien (when no PO chosen or filter off)
      return base.filter((s) => (s.client_name || "") === cfg.client_name);
    }
    return base;
  })();

  const confirmPickedSjs = () => {
    setSelectedSj((prev) => [...prev, ...sjPickerSelection]);
    setSjPickerSelection([]);
    setSjPickerOpen(false);
    toast.success(`${sjPickerSelection.length} Surat Jalan ditarik`);
  };

  const removeSj = (entry) => {
    if (entry.kind === "db") {
      setSelectedSj((prev) => prev.filter((x) => x !== entry.id));
    } else {
      const next = (cfg.manual_sjs || []).filter((_, i) => i !== entry.idx);
      setCfg((c) => ({ ...c, manual_sjs: next }));
    }
  };

  const openManualNew = () => { setManualEditIdx(null); setManualForm(blankManual); setManualOpen(true); };
  const openManualEdit = (entry) => {
    setManualEditIdx(entry.idx);
    setManualForm({ ...blankManual, ...entry, items: entry.items?.length ? entry.items : blankManual.items });
    setManualOpen(true);
  };
  const saveManual = () => {
    if (!manualForm.sj_number) return toast.error("No Surat Jalan wajib diisi");
    const cleaned = {
      ...manualForm,
      items: (manualForm.items || []).map((i) => ({
        material_name: i.material_name || "",
        unit: i.unit || "",
        quantity: parseQty(i.quantity),
      })),
    };
    const next = [...(cfg.manual_sjs || [])];
    if (manualEditIdx == null) next.push(cleaned);
    else next[manualEditIdx] = cleaned;
    setCfg((c) => ({ ...c, manual_sjs: next }));
    setManualOpen(false);
    toast.success(manualEditIdx == null ? "SJ manual ditambahkan" : "SJ manual diperbarui");
  };

  const sortBySjNumber = () => {
    const cmp = (a, b) => String(a || "").localeCompare(String(b || ""), undefined, { numeric: true, sensitivity: "base" });
    const sortedIds = [...selectedSj].sort((a, b) => cmp(sjs.find((x) => x.id === a)?.sj_number, sjs.find((x) => x.id === b)?.sj_number));
    setSelectedSj(sortedIds);
    const sortedManual = [...(cfg.manual_sjs || [])].sort((a, b) => cmp(a.sj_number, b.sj_number));
    setCfg((c) => ({ ...c, manual_sjs: sortedManual }));
    toast.success("Diurutkan berdasarkan No SJ");
  };
  const sortByDate = () => {
    const cmp = (a, b) => String(a || "").localeCompare(String(b || ""));
    const sortedIds = [...selectedSj].sort((a, b) => cmp(sjs.find((x) => x.id === a)?.date, sjs.find((x) => x.id === b)?.date));
    setSelectedSj(sortedIds);
    const sortedManual = [...(cfg.manual_sjs || [])].sort((a, b) => cmp(a.date, b.date));
    setCfg((c) => ({ ...c, manual_sjs: sortedManual }));
    toast.success("Diurutkan berdasarkan Tanggal");
  };

  // Auto-derive invoice items from SJ items (group by material+unit, sum qty).
  // Preserves user-edited unit_price; otherwise looks up from PO selected at stage 0
  // (Iter v34s #7 — bugfix: previously used price list first which mismatched PO input).
  // Iter v34co (#1) — Fix "SUM ala Excel": SELALU jumlahkan `it.quantity` mentah
  // (jangan pakai `qty_billing`) supaya total di preview cocok dengan total filter SJ.
  // Iter v34co (#2) — Urutkan material di preview mengikuti urutan input material di PO.
  useEffect(() => {
    // Iter v34{ag} BUGFIX — DP Invoice mode does NOT derive from SJ. Skipping this
    // effect prevents wiping the "Uang Muka" item we set in next() when the user
    // picks DP + percentage (Subtotal was showing Rp 0 in Stage 2 preview).
    if (cfg.is_dp_invoice) return;
    const grouped = {};
    for (const s of combinedSjs) {
      for (const it of (s.items || [])) {
        const name = (it.material_name || "").trim();
        const unit = (it.unit || "").trim();
        if (!name && !unit) continue;
        const key = `${name.toLowerCase()}|${unit.toLowerCase()}`;
        grouped[key] = grouped[key] || { material_name: name, unit, quantity: 0, unit_price: 0 };
        // Iter v34co (#1) — SUM ala Excel: pakai `quantity` mentah, abaikan qty_billing override.
        // Iter v34co (#5) — Bulatkan ke 2 desimal SEBELUM sum, supaya total = SUM(display value)
        // seperti Excel. Sebelumnya presisi internal (7.4123456 dsb) diakumulasi → total
        // meleset 0.02 dari sum manual user berdasarkan display 2-desimal.
        const rawQ = parseQty(it.quantity);
        const displayQ = Math.round(rawQ * 100) / 100;
        grouped[key].quantity += displayQ;
      }
    }
    // Iter v34cb (#3) — Bila tidak ada SJ tapi PO dipilih, pre-fill items dari PO.
    // Ini menghandle kasus: user pilih PO + tanpa SJ (tidak pakai bill_direct_from_po),
    // sampai di stage preview item kosong. Auto-fill agar user bisa langsung edit.
    if (Object.keys(grouped).length === 0 && cfg.po_id && !editingId) {
      const selPo = pos.find((p) => p.id === cfg.po_id);
      if (selPo && Array.isArray(selPo.items)) {
        for (const it of selPo.items) {
          const name = (it.material_name || "").trim();
          const unit = (it.unit || "").trim();
          if (!name && !unit) continue;
          const key = `${name.toLowerCase()}|${unit.toLowerCase()}`;
          grouped[key] = {
            material_name: name, unit,
            quantity: Number(it.quantity) || 0,
            unit_price: Number(it.unit_price) || 0,
          };
        }
      }
    }
    // Iter v34s #7 — Build PO price lookup from the SELECTED PO first
    // Iter v34co (#2) — juga bangun poOrderMap untuk sort preview items sesuai urutan PO.
    const selectedPo = pos.find((po) => po.id === cfg.po_id);
    const poPriceMap = {};
    const poOrderMap = {};
    if (selectedPo) {
      (selectedPo.items || []).forEach((it, idx) => {
        const k = `${(it.material_name || "").toLowerCase()}|${(it.unit || "").toLowerCase()}`;
        poPriceMap[k] = parseFloat(it.unit_price || 0);
        poOrderMap[k] = idx;
      });
    }
    setItems((prev) => {
      const prevMap = {};
      // Iter v34cz (#1) — `src_key` menyimpan key asal (material|unit dari SJ/PO) sehingga
      // nama/keterangan yang DIUBAH manual di preview tidak ditimpa saat re-derive,
      // namun qty & harga tetap mengikuti SJ/PO.
      const prevBySrc = {};
      (prev || []).forEach((p) => {
        const k = `${(p.material_name || "").toLowerCase()}|${(p.unit || "").toLowerCase()}`;
        prevMap[k] = p;
        if (p.src_key) prevBySrc[p.src_key] = p;
      });
      // Iter v34co (#2) — Sort entries by PO order; items tidak ada di PO ditaruh belakang.
      const sortedEntries = Object.entries(grouped).sort(([ka], [kb]) => {
        const ia = ka in poOrderMap ? poOrderMap[ka] : 9999;
        const ib = kb in poOrderMap ? poOrderMap[kb] : 9999;
        return ia - ib;
      });
      // Iter v34cz (#1) — Jangan kosongkan items yang sudah dimuat (mode edit invoice)
      // saat SJ terkait masih dimuat async → sebelumnya label & harga hasil edit hilang.
      if (sortedEntries.length === 0 && (prev || []).length > 0) return prev;
      return sortedEntries.map(([key, g]) => {
        const prevItem = prevMap[key] || prevBySrc[key];
        let unit_price = prevItem?.unit_price ? parsePrice(prevItem.unit_price) : 0;
        if (!unit_price) {
          // Iter v34s #7 — PRIORITAS: harga dari PO yang dipilih di tahap 1. Jika tidak ada,
          // baru fallback ke Daftar Harga (pricelist).
          if (poPriceMap[key]) {
            unit_price = poPriceMap[key];
          } else {
            const p = prices.find((pp) =>
              (pp.material_name || "").toLowerCase() === g.material_name.toLowerCase() &&
              (!pp.unit || pp.unit.toLowerCase() === g.unit.toLowerCase())
            );
            if (p) unit_price = parsePrice(p.price_non_vat || p.price_ex_vat || p.price_with_vat || 0);
          }
        }
        // Iter v34bu (#8) — Qty di-STORE raw (tanpa rounding) supaya perhitungan
        // subtotal/DPP/PPN akurat saat banyak SJ. Display tetap 2 desimal via formatQty.
        // Strip FP artifacts (0.30000000000000004 → 0.3) dgn round ke 6 decimals — kurang
        // dari itu bisa hilangkan data asli qty 3-4 desimal, lebih dari itu percuma.
        const rawQty = Math.round((g.quantity || 0) * 1e6) / 1e6;
        // Iter v34cz (#1) — Pertahankan label hasil edit manual (nama material / satuan).
        const renamed = prevItem && prevItem.src_key === key;
        return {
          ...g,
          src_key: key,
          material_name: renamed ? prevItem.material_name : g.material_name,
          unit: renamed ? prevItem.unit : g.unit,
          quantity: rawQty,
          unit_price,
        };
      });
    });
  }, [combinedSjs, prices, cfg.po_id, pos, cfg.is_dp_invoice]);

  const lookupPoPrice = (material_name, unit) => {
    // Iter v34s #7 — Prioritas PO yang dipilih di stage 0, baru fallback ke Daftar Harga.
    const selectedPo = pos.find((po) => po.id === cfg.po_id);
    if (selectedPo) {
      for (const it of (selectedPo.items || [])) {
        if ((it.material_name || "").toLowerCase() === (material_name || "").toLowerCase() &&
            (!unit || (it.unit || "").toLowerCase() === (unit || "").toLowerCase())) {
          return parseFloat(it.unit_price || 0);
        }
      }
    }
    // Fallback 1: scan all PO items (in case user switched PO mid-flow)
    for (const po of pos) {
      for (const it of (po.items || [])) {
        if ((it.material_name || "").toLowerCase() === (material_name || "").toLowerCase() &&
            (!unit || (it.unit || "").toLowerCase() === (unit || "").toLowerCase())) {
          return parseFloat(it.unit_price || 0);
        }
      }
    }
    // Fallback 2: Daftar Harga
    const p = prices.find((pp) =>
      (pp.material_name || "").toLowerCase() === (material_name || "").toLowerCase() &&
      (!unit || !pp.unit || pp.unit.toLowerCase() === (unit || "").toLowerCase())
    );
    if (p) return parseFloat(p.price_non_vat || p.price_ex_vat || p.price_with_vat || 0);
    return 0;
  };

  // Calculations
  const subtotal = items.reduce((s, i) => s + parseQty(i.quantity) * parsePrice(i.unit_price), 0);
  const discount = cfg.use_discount ? parseIDR(cfg.discount_amount) : 0;
  const afterDiscount = subtotal - discount;
  const dpp = cfg.use_vat && cfg.use_dpp_nilai_lain ? afterDiscount * 11 / 12 : afterDiscount;
  const vat = cfg.use_vat ? (dpp * parseFloat(cfg.vat_rate || 0)) / 100 : 0;
  const pph = cfg.use_pph ? (afterDiscount * parseFloat(cfg.pph_rate || 0)) / 100 : 0;
  const dpBase = cfg.use_dp ? afterDiscount : 0;
  // Iter v34{ad} — Uang Muka 2 mode: percentage of afterDiscount OR manual nominal.
  const dp = cfg.use_dp
    ? (cfg.dp_mode === "percentage"
        ? Math.max(0, Math.min(100, parseFloat(cfg.dp_percentage || 0))) * dpBase / 100
        : parseIDR(cfg.dp_amount))
    : 0;
  // Iter v34{ah} — DP invoice bills only the Uang Muka amount.
  // Compute dpInvoiceAmt from percentage of PO items (subtotal here) or nominal.
  const dpInvoiceAmt = cfg.is_dp_invoice
    ? (cfg.dp_invoice_mode === "percentage"
        ? Math.max(0, Math.min(100, parseFloat(cfg.dp_invoice_percentage || 0))) * subtotal / 100
        : parseIDR(cfg.dp_invoice_amount || 0))
    : 0;
  // Iter v34cz (#2) — PPN pada Invoice Uang Muka (ala Coretax): nilai DP menjadi DPP,
  // PPN dihitung di ATAS nilai DP. DPP Nilai Lain tetap berlaku (DP × 11/12).
  const dpInvDpp = cfg.is_dp_invoice && cfg.use_vat && cfg.use_dpp_nilai_lain ? dpInvoiceAmt * 11 / 12 : dpInvoiceAmt;
  const dpInvVat = cfg.is_dp_invoice && cfg.use_vat ? (dpInvDpp * parseFloat(cfg.vat_rate || 0)) / 100 : 0;
  // Iter v34da — Potongan PPN uang muka pada tagihan pelunasan (baris terpisah).
  const dpVatDeduct = !cfg.is_dp_invoice && cfg.use_dp ? parseIDR(cfg.dp_vat_amount || 0) : 0;
  const grand = cfg.is_dp_invoice ? (dpInvoiceAmt + dpInvVat) : (afterDiscount + vat - pph - dp - dpVatDeduct);

  const next = () => {
    // Iter v34bn — When "Bill Direct from PO" is ON, auto-populate items dari PO
    // dan langsung skip SJ picker (Stage 0 → 2). Beda dgn is_dp_invoice: TIDAK ada
    // perhitungan Uang Muka khusus; invoice full amount seperti Regular billing tapi tanpa SJ.
    // Iter v34cb (#2) — Saat editing, JANGAN overwrite items dari PO. Item sudah
    // di-preload dari invoice existing (line 119). Skip auto-fill dari PO.
    if (stage === 0 && cfg.bill_direct_from_po && !cfg.is_dp_invoice) {
      const selPo = pos.find((p) => p.id === cfg.po_id);
      if (!selPo) {
        toast.error("Pilih PO terlebih dahulu.");
        return;
      }
      const poItems = Array.isArray(selPo.items) ? selPo.items : [];
      if (poItems.length === 0) {
        toast.error("PO ini belum memiliki item. Tambahkan item pada PO terlebih dahulu.");
        return;
      }
      if (!editingId) {
        setItems((prev) => {
          const prevBySrc = {};
          (prev || []).forEach((p) => { if (p.src_key) prevBySrc[p.src_key] = p; });
          return poItems.map((it) => {
            const key = `${(it.material_name || "").toLowerCase()}|${(it.unit || "").toLowerCase()}`;
            const kept = prevBySrc[key];
            return {
              src_key: key,
              material_name: kept ? kept.material_name : (it.material_name || ""),
              unit: kept ? kept.unit : (it.unit || ""),
              quantity: Number(it.quantity) || 0,
              unit_price: Number(it.unit_price) || 0,
            };
          });
        });
      }
      setSelectedSj([]);
      setStage(2);
      return;
    }
    // Iter v34{ad} — When moving from Stage 0 → 1 with DP invoice mode, auto-fill items
    // and skip SJ picker step (jump straight to preview stage 2).
    if (stage === 0 && cfg.is_dp_invoice) {
      // Iter v34{ah} — Populate items from PO breakdown so the invoice shows
      // the FULL PO context (material/unit/qty/harga). Uang Muka is applied
      // as a summary-only deduction (dp_amount + dp_percentage stored on invoice).
      const selPo = pos.find((p) => p.id === cfg.po_id);
      if (!selPo) {
        toast.error("Pilih PO terlebih dahulu.");
        return;
      }
      const poItems = Array.isArray(selPo.items) ? selPo.items : [];
      if (poItems.length === 0) {
        toast.error("PO ini belum memiliki item. Tambahkan item pada PO terlebih dahulu.");
        return;
      }
      const poTotal = Number(selPo.po_total || 0) ||
        poItems.reduce((a, it) => a + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
      let dpAmt;
      if (cfg.dp_invoice_mode === "percentage") {
        const pct = Math.max(0, Math.min(100, parseFloat(cfg.dp_invoice_percentage || 0)));
        if (poTotal <= 0) {
          toast.error("Total PO harus > 0 untuk mode persentase.");
          return;
        }
        if (pct <= 0) {
          toast.error("Persentase harus > 0.");
          return;
        }
        dpAmt = poTotal * pct / 100;
      } else {
        dpAmt = parseIDR(cfg.dp_invoice_amount || 0);
      }
      if (dpAmt <= 0) {
        toast.error("Nominal Uang Muka wajib diisi (> 0).");
        return;
      }
      // Copy PO items as invoice items (material_name, unit, quantity, unit_price).
      // Iter v34cz (#1) — Pertahankan label hasil edit manual (src_key) & jangan
      // timpa item saat mode edit invoice DP.
      if (!editingId) {
        setItems((prev) => {
          const prevBySrc = {};
          (prev || []).forEach((p) => { if (p.src_key) prevBySrc[p.src_key] = p; });
          return poItems.map((it) => {
            const key = `${(it.material_name || "").toLowerCase()}|${(it.unit || "").toLowerCase()}`;
            const kept = prevBySrc[key];
            return {
              src_key: key,
              material_name: kept ? kept.material_name : (it.material_name || ""),
              unit: kept ? kept.unit : (it.unit || ""),
              quantity: Number(it.quantity) || 0,
              unit_price: Number(it.unit_price) || 0,
            };
          });
        });
      }
      setSelectedSj([]);
      setStage(2);
      return;
    }
    setStage((s) => Math.min(STAGES.length - 1, s + 1));
  };
  const back = () => {
    // Iter v34bn — Skip SJ picker on back when direct-from-PO or DP invoice.
    if (stage === 2 && (cfg.bill_direct_from_po || cfg.is_dp_invoice)) {
      setStage(0);
      return;
    }
    setStage((s) => Math.max(0, s - 1));
  };

  const [finishing, setFinishing] = useState(false);

  const finish = async () => {
    if (finishing) return;
    setFinishing(true);
    try {
      // Pre-flight validation — fail fast with a clear message before hitting backend
      if (!cfg.client_name && !cfg.client_id) {
        toast.error("Klien wajib dipilih (tahap 1).");
        setStage(0);
        return;
      }
      if (!items || items.length === 0) {
        toast.error("Minimal 1 item harus ada di tahap preview.");
        return;
      }
      const payload = {
        ...cfg,
        // Iter v34{ad} — DP invoice flag pass-through for backend guard skip.
        is_dp_invoice: !!cfg.is_dp_invoice,
        discount_amount: cfg.use_discount ? parseIDR(cfg.discount_amount) : 0,
        // Iter v34{ah} — For DP invoices, dp_amount = the Uang Muka being billed
        // (derived from % of PO items subtotal OR nominal). Backend uses this to
        // set grand_total = dp_amount for is_dp_invoice.
        dp_amount: cfg.is_dp_invoice
          ? dpInvoiceAmt
          : (cfg.use_dp ? (cfg.dp_mode === "percentage"
              ? Math.max(0, Math.min(100, parseFloat(cfg.dp_percentage || 0))) * (subtotal - (cfg.use_discount ? parseIDR(cfg.discount_amount) : 0)) / 100
              : parseIDR(cfg.dp_amount)) : 0),
        // Iter v34{af} — For standalone DP invoices, propagate mode/percentage so the PDF
        // renders "Uang Muka (X%)" and the record retains its derivation for audit.
        dp_mode: cfg.is_dp_invoice
          ? (cfg.dp_invoice_mode || "amount")
          : (cfg.use_dp ? (cfg.dp_mode || "amount") : "amount"),
        dp_percentage: cfg.is_dp_invoice
          ? (cfg.dp_invoice_mode === "percentage" ? parseFloat(cfg.dp_invoice_percentage || 0) : 0)
          : (cfg.use_dp && cfg.dp_mode === "percentage" ? parseFloat(cfg.dp_percentage || 0) : 0),
        dp_vat_amount: cfg.is_dp_invoice ? 0 : (cfg.use_dp ? parseIDR(cfg.dp_vat_amount || 0) : 0),
        invoice_number: cfg.invoice_number || "",
        items: items.map((it) => ({ ...it, quantity: parseQty(it.quantity), unit_price: parsePrice(it.unit_price) })),
        sj_ids: selectedSj,
        notes,
      };
      delete payload._suggested_number;
      // Iter16 #4: never let the client send a stale po_number — backend re-resolves
      // it from po_id so the rendered PDF always matches the PO selected in Stage 0.
      delete payload.po_number;
      let r;
      if (editingId) {
        try {
          await api.patch(`/invoices/${editingId}`, payload);
        } catch (patchErr) {
          // If patch fails with 404 (deleted), fall through to create new instead of blocking
          if (patchErr?.response?.status === 404) {
            r = await api.post("/invoices", payload);
          } else { throw patchErr; }
        }
        if (!r) r = await api.get(`/invoices/${editingId}`);
      } else {
        r = await api.post("/invoices", payload);
      }
      if (!r?.data || !r.data.invoice_number) {
        throw new Error("Respons server tidak valid (invoice_number kosong).");
      }
      // Best-effort autocomplete history — never block stage transition
      try {
        const histPairs = [
          { field: "invoice.client_name", value: cfg.client_name },
          { field: "invoice.notes", value: cfg.notes },
          // Iter v34cf — history untuk field frequent-typed
          { field: "invoice.due_days", value: cfg.due_days },
          { field: "invoice.invoice_number", value: cfg.invoice_number },
          { field: "invoice.invoice_note", value: cfg.invoice_note },
          { field: "invoice.receipt_description", value: cfg.receipt_description },
        ];
        (items || []).forEach((it) => {
          histPairs.push({ field: "invoice.material_name", value: it.material_name });
        });
        recordHistoryMany(histPairs);
      } catch { /* ignore */ }
      setCreatedInvoice(r.data);
      toast.success("Invoice " + r.data.invoice_number + (editingId ? " diperbarui" : " dibuat"));
      // Force stage transition AFTER state update with a microtask to ensure
      // createdInvoice is committed before the stage-3 render guard.
      setStage(3);
    } catch (e) {
      // Surface the actual backend detail so user knows why it failed.
      const detail = e?.response?.data?.detail
        || (Array.isArray(e?.response?.data?.detail) ? JSON.stringify(e.response.data.detail) : "")
        || e?.message
        || "Network / server error";
      console.error("[Buat Tagihan finish] failed:", e?.response?.status, e?.response?.data || e);
      toast.error("Gagal menyimpan tagihan: " + detail);
    } finally {
      setFinishing(false);
    }
  };

  const printInvoice = () => {
    if (!createdInvoice) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${createdInvoice.id}/pdf`;
    window.open(url, "_blank");
  };

  const downloadInvoicePdf = async () => {
    if (!createdInvoice) return;
    try {
      const r = await api.get(`/invoices/${createdInvoice.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `Invoice-${createdInvoice.invoice_number || createdInvoice.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      toast.success("PDF tersimpan ke perangkat");
    } catch (e) { toast.error("Gagal download PDF"); }
  };

  const uploadFaktur = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setFakturUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "faktur");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCfg({ ...cfg, faktur_file_path: r.data.path, faktur_file_id: r.data.id, faktur_file_name: r.data.filename, faktur_file_content_type: r.data.content_type });
      toast.success("Faktur Pajak terunggah");
    } catch (err) { toast.error("Gagal upload"); }
    finally { setFakturUploading(false); }
  };

  const reset = () => {
    // Full reset — clear faktur, manual SJs, items, editingId, and any other per-invoice state
    // so a fresh "Buat invoice baru" does NOT inherit or overwrite the previous invoice.
    // Iter v34cb (#4) — Bug: sebelumnya editingId tidak di-clear → "Buat invoice baru"
    // malah menimpa invoice sebelumnya (submit ke PATCH endpoint).
    setStage(0);
    setEditingId(null);
    setCfg({
      ...BLANK_CFG,
      // Keep convenience fields (company/bank/signatory) so user doesn't re-pick each time
      company_id: cfg.company_id,
      bank_account_id: cfg.bank_account_id,
      signatory: cfg.signatory,
    });
    setItems([]);
    setSelectedSj([]);
    setNotes("");
    setCreatedInvoice(null);
    setSjPickerSelection([]);
    // Also drop the ?edit= URL param so subsequent refresh doesn't reload old invoice.
    if (window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.delete("edit");
      window.history.replaceState({}, "", url.toString());
    }
    setPoTouched(false);
  };

  // Iter v34cf — Sequential form guards: Klien → Tanggal → PO. Bypass saat edit invoice.
  const isEditing = !!editingId;
  // Iter v34cu (#4) — Tanggal jatuh tempo efektif (auto = tanggal invoice + due_days).
  const effectiveDueDate = (() => {
    if (cfg.due_date) return cfg.due_date;
    if (!cfg.invoice_date) return "";
    const d = new Date(`${cfg.invoice_date}T00:00:00`);
    d.setDate(d.getDate() + (Number(cfg.due_days) || 14));
    return d.toISOString().slice(0, 10);
  })();

  const clientDone = !!(cfg.client_id || cfg.client_name);
  const dateDone = clientDone && !!cfg.invoice_date;
  const poDone = dateDone && (poTouched || !!cfg.po_id);
  const dateLocked = !isEditing && !clientDone;
  const poLocked = !isEditing && !dateDone;
  const sequentialLocked = !isEditing && !poDone;

  return (
    <div className="max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <PageHeader
        overline="Keuangan · Penagihan"
        title={t("billing_invoice")}
        description="Wizard 4 tahap: konfigurasi → item → preview → konfirmasi. Termasuk PPN, PPH, Diskon, dan Uang Muka."
      />

      {/* Stepper */}
      <div className="flex items-center justify-between gap-2">
        {STAGES.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-3 ${i <= stage ? "text-stone-900" : "text-stone-400"}`}>
              <div className={`h-7 w-7 flex items-center justify-center text-xs font-mono ${i <= stage ? "bg-terra-500 text-white" : "bg-stone-100"}`}>{i + 1}</div>
              <span className="text-xs uppercase tracking-widest font-semibold whitespace-nowrap">{t(s)}</span>
            </div>
            {i < STAGES.length - 1 && <div className={`flex-1 h-px ${i < stage ? "bg-terra-500" : "bg-stone-200"}`} />}
          </React.Fragment>
        ))}
      </div>

      {stage === 0 && (
        <Card className="p-6 space-y-6">
          <h2 className="font-display text-lg font-semibold">{t("config_stage")}</h2>

          {/* Iter v34cf — Setup Awal: Klien → Tanggal → PO wajib berurutan. */}
          {!isEditing && (
            <div className="bg-terra-50 border border-terra-200 p-4 rounded-sm space-y-4" data-testid="setup-awal-section">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] uppercase tracking-[0.2em] text-terra-700 font-semibold">Setup Awal · 3 Langkah Wajib Berurutan</p>
                <div className="flex items-center gap-2 text-[11px] font-mono">
                  <span className={`px-2 py-0.5 rounded-full border ${clientDone ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-white border-stone-300 text-stone-500"}`} data-testid="step-client-status">1. Klien {clientDone ? "✓" : "•"}</span>
                  <span className={`px-2 py-0.5 rounded-full border ${dateDone ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-white border-stone-300 text-stone-500"}`} data-testid="step-date-status">2. Tanggal {dateDone ? "✓" : "•"}</span>
                  <span className={`px-2 py-0.5 rounded-full border ${poDone ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-white border-stone-300 text-stone-500"}`} data-testid="step-po-status">3. PO {poDone ? "✓" : "•"}</span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Step 1: Klien */}
                <Field label="1. Klien Penagihan" required>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-[11px] text-stone-600">
                      <input type="checkbox" checked={clientManual} onChange={(e) => setClientManual(e.target.checked)} data-testid="cfg-client-manual-toggle" /> Input Manual
                    </label>
                    {clientManual ? (
                      <Input value={cfg.client_name} onChange={(e) => setCfg({ ...cfg, client_id: "", client_name: e.target.value })} placeholder="Nama klien" data-testid="cfg-client-manual" />
                    ) : (
                      <Select value={cfg.client_id} onChange={(e) => {
                        const c = clients.find((x) => x.id === e.target.value);
                        setCfg({ ...cfg, client_id: e.target.value, client_name: c?.name || "" });
                      }} data-testid="cfg-client-dropdown">
                        <option value="">{cfg.client_name ? `(Auto: ${cfg.client_name})` : "— Pilih klien —"}</option>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </Select>
                    )}
                  </div>
                </Field>
                {/* Step 2: Tanggal */}
                <Field label="2. Tanggal Pembuatan" required hint={dateLocked ? "Isi Klien dulu" : ""}>
                  <DatePicker value={cfg.invoice_date} disabled={dateLocked} onChange={(e) => setCfg({ ...cfg, invoice_date: e.target.value })} data-testid="cfg-invoice-date" />
                </Field>
                {/* Step 3: PO */}
                <Field label="3. Pilih PO" required hint={poLocked ? "Isi Tanggal dulu" : "Pilih PO atau 'Tanpa PO' lalu klik"}>
                  <Select value={cfg.po_id} disabled={poLocked} onChange={(e) => {
                    const newPoId = e.target.value;
                    setPoTouched(true);
                    setCfg({ ...cfg, po_id: newPoId, bill_direct_from_po: newPoId ? cfg.bill_direct_from_po : false });
                  }} data-testid="billing-po-select">
                    <option value="">— Tanpa PO —</option>
                    {pos.filter((p) => {
                      // Iter v34ck — Filter PO by klien penagihan (sinkron dgn Setup Awal Step 1).
                      if (p.is_closed) return false;
                      if (p.fully_billed && p.id !== cfg.po_id) return false;
                      // Compute nama klien dari cfg (dropdown id ATAU manual name)
                      const selClient = cfg.client_id ? (clients.find((c) => c.id === cfg.client_id) || {}) : {};
                      const targetName = ((selClient.name || cfg.client_name || "").trim().toLowerCase());
                      if (!targetName) return true; // Belum pilih klien — jangan filter
                      const poName = (p.client_name || "").trim().toLowerCase();
                      const matchByName = poName && poName === targetName;
                      const matchById = cfg.client_id && p.client_id && p.client_id === cfg.client_id;
                      if (!matchByName && !matchById && p.id !== cfg.po_id) return false;
                      return true;
                    }).map((p) => <option key={p.id} value={p.id}>{p.po_number} · {p.client_name}</option>)}
                  </Select>
                  {!poLocked && !cfg.po_id && !poTouched && (
                    <button type="button" onClick={() => setPoTouched(true)} className="mt-1 text-[11px] text-terra-700 underline hover:text-terra-800" data-testid="cfg-po-confirm-tanpa">
                      Konfirmasi &quot;Tanpa PO&quot; untuk lanjut →
                    </button>
                  )}
                </Field>
              </div>
              {sequentialLocked && (
                <p className="text-[11px] text-stone-500 italic" data-testid="sequential-lock-hint">
                  Field lain akan aktif setelah 3 langkah di atas terisi.
                </p>
              )}
            </div>
          )}

          <fieldset disabled={sequentialLocked} className={sequentialLocked ? "opacity-50 pointer-events-none space-y-6" : "space-y-6"} data-testid="billing-cfg-fieldset">

          {/* Iter v34bn — Invoice langsung dari PO (tanpa SJ). Skip SJ picker, auto-populate items dari PO. */}
          {/* Iter v34bw (#7) — Checkbox HANYA aktif kalau PO sudah dipilih. Auto-uncheck saat user pilih "Tanpa PO". */}
          <div className={`bg-sky-50 border border-sky-200 p-4 rounded-sm ${(!cfg.po_id || !!cfg.is_dp_invoice) ? "opacity-60" : ""}`}>
            <label className={`flex items-center gap-2 text-sm font-medium ${(!cfg.po_id || !!cfg.is_dp_invoice) ? "cursor-not-allowed" : "cursor-pointer"}`}>
              <input type="checkbox" checked={!!cfg.bill_direct_from_po}
                onChange={(e) => setCfg({ ...cfg, bill_direct_from_po: e.target.checked, is_dp_invoice: e.target.checked ? false : cfg.is_dp_invoice })}
                disabled={!!cfg.is_dp_invoice || !cfg.po_id}
                data-testid="cfg-bill-direct-from-po" />
              Buat Invoice langsung dari <b>PO (tanpa Surat Jalan)</b>
            </label>
            {!cfg.po_id ? (
              <div className="mt-2 text-xs text-amber-700 flex items-start gap-1">
                <span>⚠</span>
                <span>Pilih PO terlebih dahulu di dropdown <b>&quot;Pilih PO&quot;</b> di bawah untuk mengaktifkan opsi ini. Untuk invoice <i>tanpa PO</i>, biarkan opsi ini tidak dicentang dan gunakan langkah <b>Item</b> berikutnya.</span>
              </div>
            ) : cfg.bill_direct_from_po ? (
              <div className="mt-2 text-xs text-sky-800">
                Item invoice akan otomatis diisi dari item PO. Klik <b>Lanjut</b> untuk langsung ke Preview.
              </div>
            ) : null}
          </div>

          {/* Iter v34{ad} — Invoice Uang Muka toggle. When ON, auto-populates items dgn 1 line "Uang Muka",
              skips SJ picker step, dan langsung ke preview. */}
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input type="checkbox" checked={!!cfg.is_dp_invoice}
                onChange={(e) => setCfg({ ...cfg, is_dp_invoice: e.target.checked, bill_direct_from_po: e.target.checked ? false : cfg.bill_direct_from_po })}
                disabled={!!cfg.bill_direct_from_po}
                data-testid="cfg-is-dp-invoice" />
              Jadikan sebagai <b>Invoice Uang Muka (Down Payment)</b>
            </label>
            {cfg.is_dp_invoice && (() => {
              const selPo = pos.find((p) => p.id === cfg.po_id);
              const poTotal = Number(selPo?.po_total || 0);
              const pct = Math.max(0, Math.min(100, parseFloat(cfg.dp_invoice_percentage || 0)));
              const computedFromPct = poTotal * pct / 100;
              return (
                <div className="mt-3 space-y-3" data-testid="dp-invoice-config">
                  {/* Iter v34{af} — Mode toggle: Nominal atau Persentase (dari total PO). */}
                  <div className="flex items-center gap-2 text-xs">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name="dp_invoice_mode"
                        checked={cfg.dp_invoice_mode !== "percentage"}
                        onChange={() => setCfg({ ...cfg, dp_invoice_mode: "amount" })}
                        data-testid="dp-mode-amount"
                      />
                      Nominal
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name="dp_invoice_mode"
                        checked={cfg.dp_invoice_mode === "percentage"}
                        onChange={() => setCfg({ ...cfg, dp_invoice_mode: "percentage" })}
                        data-testid="dp-mode-percentage"
                      />
                      Persentase dari total PO
                    </label>
                  </div>
                  {cfg.dp_invoice_mode === "percentage" ? (
                    <>
                      <Field label="Persentase Uang Muka (%)" required>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step="0.01"
                          className="font-mono-num"
                          value={cfg.dp_invoice_percentage || ""}
                          onChange={(e) => setCfg({ ...cfg, dp_invoice_percentage: e.target.value })}
                          placeholder="30"
                          data-testid="cfg-dp-invoice-percentage"
                        />
                      </Field>
                      <div className="p-2 bg-white/60 border border-amber-200 rounded-sm text-xs" data-testid="dp-percentage-preview">
                        {selPo && poTotal > 0 ? (
                          <>
                            Total PO: <b className="font-mono-num">{formatIDR(poTotal)}</b>
                            <br />
                            Perhitungan: <span className="font-mono-num">{formatIDR(poTotal)}</span> × <b>{pct}%</b> = <b className="font-mono-num text-amber-700">{formatIDR(computedFromPct)}</b>
                          </>
                        ) : (
                          <span className="text-red-600">Pilih PO terlebih dahulu untuk perhitungan otomatis (Nilai Total PO harus &gt; 0).</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <Field label="Nominal Uang Muka" required>
                      <Input className="font-mono-num" value={cfg.dp_invoice_amount || ""}
                        onChange={(e) => setCfg({ ...cfg, dp_invoice_amount: e.target.value })}
                        placeholder="Rp"
                        data-testid="cfg-dp-invoice-amount" />
                    </Field>
                  )}
                  <p className="text-xs text-stone-600">Invoice ini akan berisi satu baris item &quot;Uang Muka&quot; dan <b>tidak</b> menarik data Surat Jalan. Cocok untuk termin awal sebelum barang dikirim.</p>
                </div>
              );
            })()}
          </div>

          {/* Section: Invoice meta */}
          <div className="space-y-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Identitas Invoice</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Iter v34co (#4) — Tanggal Pembuatan selalu tampil di Identitas Invoice
                  (Setup Awal disembunyikan saat edit → sebelumnya date tidak bisa diedit). */}
              <Field label="Tanggal Pembuatan" required hint="Tanggal invoice diterbitkan">
                <DatePicker value={cfg.invoice_date} onChange={(e) => setCfg({ ...cfg, invoice_date: e.target.value })} data-testid="cfg-invoice-date-identitas" />
              </Field>
              {/* Iter v34cq (#6) — Saat EDIT invoice, No PO bisa diganti ke PO lain atau Tanpa PO. */}
              {isEditing && (
                <Field label="No PO / Kontrak" hint="Bisa diganti ke PO lain atau dikosongkan (Tanpa PO)">
                  <Select value={cfg.po_id || ""} onChange={(e) => {
                    const newPoId = e.target.value;
                    setPoTouched(true);
                    setCfg({ ...cfg, po_id: newPoId, bill_direct_from_po: newPoId ? cfg.bill_direct_from_po : false });
                  }} data-testid="cfg-po-edit-select">
                    <option value="">— Tanpa PO —</option>
                    {/* Iter v34cw (#2) — Daftar PO tetap terfilter sesuai klien invoice. */}
                    {pos
                      .filter((p) => !p.is_closed || p.id === cfg.po_id)
                      .filter((p) => !cfg.client_name || p.id === cfg.po_id || (p.client_name || "").toLowerCase() === (cfg.client_name || "").toLowerCase())
                      .map((p) => (
                        <option key={p.id} value={p.id}>{p.po_number} · {p.client_name}</option>
                      ))}
                  </Select>
                </Field>
              )}
              {/* Iter v34cu (#4) — Satu kolom saja: Tanggal Jatuh Tempo.
                  Jumlah hari (due_days) dihitung otomatis dari tanggal yang dipilih. */}
              <Field label="Tanggal Jatuh Tempo" hint={`Otomatis ${cfg.due_days || 14} hari dari tanggal pembuatan — bisa diubah`}>
                <DatePicker
                  value={effectiveDueDate}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) { setCfg({ ...cfg, due_date: "" }); return; }
                    const d0 = cfg.invoice_date ? new Date(`${cfg.invoice_date}T00:00:00`) : null;
                    const d1 = new Date(`${v}T00:00:00`);
                    const days = d0 ? Math.max(0, Math.round((d1 - d0) / 86400000)) : (cfg.due_days || 14);
                    setCfg({ ...cfg, due_date: v, due_days: days });
                  }}
                  data-testid="cfg-due-date"
                />
              </Field>
              <Field label="No Invoice" hint={cfg._suggested_number ? `Saran: ${cfg._suggested_number}` : "Kosongkan untuk otomatis"}>
                <AutocompleteInput
                  field="invoice.invoice_number"
                  value={cfg.invoice_number}
                  onChange={(v) => setCfg({ ...cfg, invoice_number: v })}
                  placeholder={cfg._suggested_number || "auto"}
                  limit={3}
                  dataTestid="cfg-invoice-number"
                />
              </Field>

              <Field label="Recap Header">
                <Select value={cfg.recap_title} onChange={(e) => setCfg({ ...cfg, recap_title: e.target.value })} data-testid="cfg-recap-title">
                  {RECAP_TITLES.map((x) => <option key={x} value={x}>{x}</option>)}
                </Select>
              </Field>
              <Field label="Kategori">
                <MasterCombo source="categories" value={cfg.category} onChange={(v) => setCfg({ ...cfg, category: v })} placeholder="delivery, service…" data-testid="cfg-category" />
              </Field>
              <Field label="Catatan Header (Subjudul)">
                <Input value={cfg.notes_header} onChange={(e) => setCfg({ ...cfg, notes_header: e.target.value })} placeholder="Tagihan Handling Semen Jumbo Bag" />
              </Field>
            </div>
          </div>

          {/* Section: Company / Bank / Signatory */}
          <div className="space-y-4 border-t border-stone-200 pt-5">
            <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Identitas Penagih (Kop Surat)</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Pilih Company Profile" hint="Menentukan kop surat. Akan disembunyikan jika PPN tidak aktif.">
                <Select value={cfg.company_id} onChange={(e) => setCfg({ ...cfg, company_id: e.target.value })} data-testid="cfg-company">
                  <option value="">— Pilih perusahaan —</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}{c.is_default ? " (default)" : ""}</option>)}
                </Select>
              </Field>
              <Field label="Rekening Bank">
                <Select value={cfg.bank_account_id} onChange={(e) => setCfg({ ...cfg, bank_account_id: e.target.value })} data-testid="cfg-bank">
                  <option value="">— Pilih bank —</option>
                  {banks.filter((b) => !cfg.company_id || !b.company_id || b.company_id === cfg.company_id).map((b) =>
                    <option key={b.id} value={b.id}>{b.bank_name} · {b.account_no}</option>
                  )}
                </Select>
              </Field>
              <Field label="Penanda Tangan">
                {signatories.length > 0 ? (
                  <Select value={cfg.signatory} onChange={(e) => {
                    const s = signatories.find((x) => x.name === e.target.value);
                    setCfg({ ...cfg, signatory: e.target.value, signatory_location: s?.location || "", signatory_position: s?.position || "" });
                  }} data-testid="cfg-signatory">
                    <option value="">— Pilih —</option>
                    {signatories.map((s) => <option key={s.id} value={s.name}>{s.name}{s.position ? ` · ${s.position}` : ""}</option>)}
                  </Select>
                ) : (
                  <Input value={cfg.signatory} onChange={(e) => setCfg({ ...cfg, signatory: e.target.value })} placeholder="Nama penanda tangan" data-testid="cfg-signatory" />
                )}
              </Field>
            </div>
          </div>

          {/* Section: Display flags */}
          <div className="space-y-3 border-t border-stone-200 pt-5">
            <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Tampilan Recap</p>
            <div className="flex flex-wrap gap-5">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={cfg.show_sj_no} onChange={(e) => setCfg({ ...cfg, show_sj_no: e.target.checked })} data-testid="cfg-show-sj" />
                Tampilkan No Surat Jalan
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={cfg.show_vehicle_no} onChange={(e) => setCfg({ ...cfg, show_vehicle_no: e.target.checked })} data-testid="cfg-show-vehicle" />
                Tampilkan No Kendaraan
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={cfg.show_destination} onChange={(e) => setCfg({ ...cfg, show_destination: e.target.checked })} data-testid="cfg-show-destination" />
                Tampilkan Tujuan
              </label>
            </div>
          </div>

          {/* Section: Taxes & Adjustments */}
          <div className="space-y-3 border-t border-stone-200 pt-5">
            <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Pajak & Penyesuaian</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-2">
                  <input type="checkbox" checked={cfg.use_vat} onChange={(e) => setCfg({ ...cfg, use_vat: e.target.checked, invoice_number: "" })} data-testid="cfg-use-vat" />
                  {t("use_vat")} <span className="text-[10px] text-stone-500 font-normal">(RBB pada No Invoice)</span>
                </label>
                {cfg.use_vat && (
                  <div className="space-y-2">
                    <Input className="font-mono-num" type="number" step="0.1" value={cfg.vat_rate} onChange={(e) => setCfg({ ...cfg, vat_rate: e.target.value })} placeholder="12" data-testid="cfg-vat-rate" />
                    <label className="flex items-center gap-2 text-xs text-stone-600">
                      <input type="checkbox" checked={cfg.use_dpp_nilai_lain} onChange={(e) => setCfg({ ...cfg, use_dpp_nilai_lain: e.target.checked })} data-testid="cfg-use-dpp-nl" />
                      Gunakan DPP Nilai Lain (DPP = Subtotal × 11/12)
                    </label>
                  </div>
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-2">
                  <input type="checkbox" checked={cfg.use_discount} onChange={(e) => setCfg({ ...cfg, use_discount: e.target.checked })} data-testid="cfg-use-discount" />
                  {t("discount")}
                </label>
                {cfg.use_discount && <Input className="font-mono-num" value={cfg.discount_amount} onChange={(e) => setCfg({ ...cfg, discount_amount: e.target.value })} placeholder="Rp" />}
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-2">
                  <input type="checkbox" checked={cfg.use_pph} onChange={(e) => setCfg({ ...cfg, use_pph: e.target.checked })} data-testid="cfg-use-pph" />
                  {t("pph")} (%)
                </label>
                {cfg.use_pph && <Input className="font-mono-num" type="number" step="0.1" value={cfg.pph_rate} onChange={(e) => setCfg({ ...cfg, pph_rate: e.target.value })} />}
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-2">
                  <input type="checkbox" checked={cfg.use_dp} onChange={(e) => setCfg({ ...cfg, use_dp: e.target.checked })} data-testid="cfg-use-dp" />
                  {t("down_payment")}
                </label>
                {/* Iter v34{ae} — Outstanding DP notice from previous DP invoices on the same PO. */}
                {dpOutstanding && !cfg.is_dp_invoice && (Number(dpOutstanding.outstanding_dp || 0) > 0 || Number(dpOutstanding.issued_dp || 0) > 0) && (
                  <div className="mb-2 p-2 rounded border border-amber-300 bg-amber-50 text-xs text-amber-900" data-testid="dp-outstanding-notice">
                    <div><b>Uang muka diterbitkan (belum dipotong):</b> {formatIDR(dpOutstanding.issued_dp)}{Number(dpOutstanding.issued_dp_vat || 0) > 0 ? ` + PPN ${formatIDR(dpOutstanding.issued_dp_vat)}` : ""}</div>
                    <div><b>Sudah dibayar klien:</b> {formatIDR(dpOutstanding.outstanding_dp)}{Number(dpOutstanding.outstanding_dp_vat || 0) > 0 ? ` + PPN ${formatIDR(dpOutstanding.outstanding_dp_vat)}` : ""}</div>
                    <div className="mt-1 flex items-center gap-3">
                      <button
                        type="button"
                        className="underline hover:text-amber-700"
                        onClick={() => setCfg({ ...cfg, use_dp: true, dp_mode: "amount", dp_amount: dpOutstanding.issued_dp, dp_vat_amount: dpOutstanding.issued_dp_vat || 0 })}
                        data-testid="dp-apply-issued-btn"
                      >
                        Potong yang diterbitkan
                      </button>
                      <button
                        type="button"
                        className="underline hover:text-amber-700 disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
                        disabled={Number(dpOutstanding.outstanding_dp || 0) <= 0}
                        onClick={() => setCfg({ ...cfg, use_dp: true, dp_mode: "amount", dp_amount: dpOutstanding.outstanding_dp, dp_vat_amount: dpOutstanding.outstanding_dp_vat || 0 })}
                        data-testid="dp-apply-btn"
                      >
                        Potong yang sudah dibayar
                      </button>
                    </div>
                  </div>
                )}
                {cfg.use_dp && (
                  <div className="space-y-2">
                    {/* Iter v34{ad} — Mode: Nominal manual atau Persentase dari (subtotal - diskon). */}
                    <div className="flex items-center gap-3 text-xs">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name="dp-mode" checked={(cfg.dp_mode || "amount") === "amount"}
                          onChange={() => setCfg({ ...cfg, dp_mode: "amount" })}
                          data-testid="cfg-dp-mode-amount" />
                        Nominal
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name="dp-mode" checked={cfg.dp_mode === "percentage"}
                          onChange={() => setCfg({ ...cfg, dp_mode: "percentage" })}
                          data-testid="cfg-dp-mode-percent" />
                        Persentase
                      </label>
                    </div>
                    {cfg.dp_mode === "percentage" ? (
                      <div className="flex items-center gap-2">
                        <Input className="font-mono-num" type="number" min="0" max="100" step="0.01"
                          value={cfg.dp_percentage}
                          onChange={(e) => setCfg({ ...cfg, dp_percentage: e.target.value })}
                          placeholder="0"
                          data-testid="cfg-dp-percentage" />
                        <span className="text-sm text-stone-600">% dari subtotal setelah diskon</span>
                      </div>
                    ) : (
                      <Input className="font-mono-num" value={cfg.dp_amount}
                        onChange={(e) => setCfg({ ...cfg, dp_amount: e.target.value })}
                        placeholder="Rp"
                        data-testid="cfg-dp-amount" />
                    )}
                    {cfg.dp_mode === "percentage" && cfg.dp_percentage > 0 && (
                      <p className="text-xs text-stone-500 font-mono-num">
                        = {formatIDR(Math.max(0, Math.min(100, parseFloat(cfg.dp_percentage || 0))) * afterDiscount / 100)}
                      </p>
                    )}
                    {/* Iter v34da — Potongan PPN uang muka (baris terpisah di Ringkasan & PDF) */}
                    <div className="pt-1">
                      <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">PPN Uang Muka (dipotong)</label>
                      <Input className="font-mono-num mt-1" value={cfg.dp_vat_amount}
                        onChange={(e) => setCfg({ ...cfg, dp_vat_amount: e.target.value })}
                        placeholder="Rp 0"
                        data-testid="cfg-dp-vat-amount" />
                      <p className="text-[11px] text-stone-500 mt-1">PPN yang sudah ditagih pada invoice uang muka. Isi 0 bila uang muka tanpa PPN.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section: Notes */}
          <div className="space-y-4 border-t border-stone-200 pt-5">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Catatan</p>
              {/* Iter v34{ae} — AI-translate invoice free-text fields (Bahasa <-> English). */}
              <Button
                variant="secondary"
                className="!py-1.5 !px-3 text-xs"
                onClick={async () => {
                  try {
                    const r = await api.post("/invoices/translate-notes", {
                      target_language: "en",
                      invoice_note: cfg.invoice_note || "",
                      notes: cfg.notes || "",
                      notes_header: cfg.notes_header || "",
                      receipt_description: cfg.receipt_description || "",
                    });
                    setCfg((c) => ({
                      ...c,
                      invoice_note: r.data.invoice_note || c.invoice_note,
                      notes: r.data.notes || c.notes,
                      notes_header: r.data.notes_header || c.notes_header,
                      receipt_description: r.data.receipt_description || c.receipt_description,
                    }));
                    toast.success("Catatan diterjemahkan ke English.");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Gagal menerjemahkan catatan.");
                  }
                }}
                data-testid="translate-notes-en-btn"
              >
                Translate → English
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Catatan Invoice" hint='Default: "Pembayaran jatuh tempo 14 hari"'>
                <AutocompleteInput
                  field="invoice.invoice_note"
                  value={cfg.invoice_note}
                  onChange={(v) => setCfg({ ...cfg, invoice_note: v })}
                  limit={3}
                  dataTestid="cfg-invoice-note"
                />
              </Field>
              <Field label="Keterangan Pembayaran (Kwitansi)" hint='Default: "Pelunasan tagihan"'>
                <AutocompleteInput
                  field="invoice.receipt_description"
                  value={cfg.receipt_description}
                  onChange={(v) => setCfg({ ...cfg, receipt_description: v })}
                  limit={3}
                  dataTestid="cfg-receipt-desc"
                />
              </Field>
            </div>
          </div>

          </fieldset>

          <div className="flex justify-end gap-2 pt-4 border-t border-stone-200">
            <Button onClick={next} disabled={!cfg.client_name && !cfg.client_id} data-testid="stage-next-1">{t("next")} →</Button>
          </div>
        </Card>
      )}

      {stage === 1 && (
        <Card className="p-6 space-y-5">
          <h2 className="font-display text-lg font-semibold">{t("items_stage")}</h2>
          <p className="text-xs text-stone-500">Pilih atau buat Surat Jalan yang akan ditagih. Item invoice (qty per material) akan dihitung otomatis dari total SJ pada tahap Preview.</p>

          {/* SJ Wizard Section — ALWAYS visible regardless of show_sj_no toggle (PDF column only) */}
          {true && (
            <div className="space-y-3" data-testid="sj-wizard-section">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-stone-600">Surat Jalan yang ditagih</label>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="secondary" className="!py-1.5 !px-3 text-xs" onClick={sortBySjNumber} data-testid="sort-sj-number-btn">
                    <ArrowDownAZ className="h-3.5 w-3.5" />Urutkan SJ
                  </Button>
                  <Button variant="secondary" className="!py-1.5 !px-3 text-xs" onClick={sortByDate} data-testid="sort-sj-date-btn">
                    <CalendarArrowDown className="h-3.5 w-3.5" />Urutkan Tanggal
                  </Button>
                  <Button variant="secondary" className="!py-1.5 !px-3 text-xs" onClick={openManualNew} data-testid="add-manual-sj-btn">
                    <Plus className="h-3.5 w-3.5" />Input SJ Manual
                  </Button>
                  <Button className="!py-1.5 !px-3 text-xs" onClick={() => { setSjPickerSelection([]); setSjPickerOpen(true); }} data-testid="pull-sj-btn">
                    <FileText className="h-3.5 w-3.5" />Tarik Surat Jalan
                  </Button>
                </div>
              </div>

              {combinedSjs.length === 0 ? (
                <div className="border border-dashed border-stone-300 px-4 py-6 text-center text-xs text-stone-500" data-testid="sj-wizard-empty">
                  Belum ada Surat Jalan. Klik <b>Tarik Surat Jalan</b> untuk memilih dari daftar, atau <b>Input SJ Manual</b> untuk membuat baru.
                </div>
              ) : (
                <div className="border border-stone-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-stone-50 text-left text-[10px] uppercase tracking-widest font-semibold text-stone-600 border-b border-stone-200">
                        <th className="py-2 px-3 w-8">#</th>
                        <th className="py-2 px-3">No SJ</th>
                        <th className="py-2 px-3">Tanggal</th>
                        <th className="py-2 px-3">No Kendaraan</th>
                        <th className="py-2 px-3">Ekspeditur</th>
                        <th className="py-2 px-3">Tujuan</th>
                        <th className="py-2 px-3 text-right">Qty</th>
                        <th className="py-2 px-3">Sumber</th>
                        <th className="py-2 px-3 text-right">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {combinedSjs.map((s, idx) => {
                        // Iter v34co (#5) — SUM ala Excel: round ke 2 desimal per item sebelum akumulasi.
                        const totalQty = (s.items || []).reduce((sum, it) => sum + Math.round(parseQty(it.quantity) * 100) / 100, 0);
                        return (
                          <tr key={s.key} className="border-b border-stone-100" data-testid={`sj-wizard-row-${idx}`}>
                            <td className="py-2 px-3 text-stone-500 font-mono">{idx + 1}</td>
                            <td className="py-2 px-3 font-mono font-medium">{s.sj_number}</td>
                            <td className="py-2 px-3 font-mono text-stone-600">{formatDate(s.date)}</td>
                            <td className="py-2 px-3 font-mono text-stone-600">{s.vehicle_no || "—"}</td>
                            <td className="py-2 px-3 text-stone-600" data-testid={`sj-wizard-ekspeditur-${idx}`}>{s.ekspeditur || "—"}</td>
                            <td className="py-2 px-3 text-stone-600">{s.delivery_location || "—"}</td>
                            <td className="py-2 px-3 text-right font-mono-num">{formatQty(totalQty)}</td>
                            <td className="py-2 px-3">
                              {s.kind === "manual" ? (
                                <span className="inline-flex items-center text-[10px] uppercase tracking-wider font-semibold border px-2 py-0.5 bg-amber-50 text-amber-700 border-amber-100">Manual</span>
                              ) : (
                                <span className="inline-flex items-center text-[10px] uppercase tracking-wider font-semibold border px-2 py-0.5 bg-sky-50 text-sky-700 border-sky-100">Ditarik</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {s.kind === "manual" && (
                                <button onClick={() => openManualEdit(s)} className="text-stone-500 hover:text-terra-600 px-2" title="Edit" data-testid={`sj-wizard-edit-${idx}`}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              )}
                              <button onClick={() => removeSj(s)} className="text-stone-500 hover:text-red-700 px-2" title="Hapus" data-testid={`sj-wizard-remove-${idx}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <Field label={t("notes")}><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>

          <div className="flex justify-between pt-4">
            <Button variant="ghost" onClick={back}>← {t("back")}</Button>
            <Button onClick={next} data-testid="stage-next-2">{t("next")} →</Button>
          </div>
        </Card>
      )}

      {stage === 2 && (
        <Card className="p-0">
          <div className="grid grid-cols-1 lg:grid-cols-3">
            <div className="lg:col-span-2 p-6 space-y-4">
              <h2 className="font-display text-lg font-semibold">{t("preview_stage")}</h2>
              <p className="text-xs text-stone-500">QTY dihitung otomatis dari total Surat Jalan pada tahap sebelumnya. Harga Satuan dapat diisi manual atau diambil dari Daftar Harga / PO.</p>
              <div className="border border-stone-200 overflow-x-auto">
                <table className="w-full text-sm" data-testid="preview-items-table">
                  <thead>
                    <tr className="bg-stone-50 text-left text-[10px] uppercase tracking-widest font-semibold text-stone-600 border-b border-stone-200">
                      <th className="py-2 px-3 w-8">#</th>
                      <th className="py-2 px-3">Material</th>
                      <th className="py-2 px-3 w-24">Satuan</th>
                      <th className="py-2 px-3 text-right w-24">Qty</th>
                      <th className="py-2 px-3 text-right w-44">Harga Satuan</th>
                      <th className="py-2 px-3 text-right w-36">Subtotal</th>
                      <th className="py-2 px-3 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 && (
                      <tr><td colSpan={7} className="py-8 text-center text-xs text-stone-400">Belum ada item. Tambahkan Surat Jalan pada tahap sebelumnya atau klik &quot;Tambah Item&quot; di bawah.</td></tr>
                    )}
                    {items.map((it, i) => {
                      const st = parseQty(it.quantity) * parsePrice(it.unit_price);
                      const suggested = lookupPoPrice(it.material_name, it.unit);
                      return (
                        <tr key={i} className="border-b border-stone-100" data-testid={`preview-row-${i}`}>
                          <td className="py-2 px-3 text-stone-500 font-mono">{i + 1}</td>
                          <td className="py-2 px-2">
                            <Input value={it.material_name} onChange={(e) => { const v = [...items]; if (!v[i].src_key) v[i].src_key = `${(v[i].material_name || "").toLowerCase()}|${(v[i].unit || "").toLowerCase()}`; v[i].material_name = e.target.value; setItems(v); }} data-testid={`preview-name-${i}`} />
                          </td>
                          <td className="py-2 px-2">
                            <Input value={it.unit} onChange={(e) => { const v = [...items]; if (!v[i].src_key) v[i].src_key = `${(v[i].material_name || "").toLowerCase()}|${(v[i].unit || "").toLowerCase()}`; v[i].unit = e.target.value; setItems(v); }} data-testid={`preview-unit-${i}`} />
                          </td>
                          <td className="py-2 px-2">
                            <Input className="font-mono-num text-right" value={it.quantity} onChange={(e) => { const v = [...items]; v[i].quantity = e.target.value; setItems(v); }} data-testid={`preview-qty-${i}`} />
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex items-center gap-1">
                              <Input className="font-mono-num text-right" value={it.unit_price} onChange={(e) => { const v = [...items]; v[i].unit_price = e.target.value; setItems(v); }} data-testid={`preview-price-${i}`} />
                              {suggested > 0 && (
                                <button
                                  type="button"
                                  onClick={() => { const v = [...items]; v[i].unit_price = suggested; setItems(v); toast.success("Harga dari PO"); }}
                                  className="text-[10px] px-1.5 py-1 border border-stone-300 hover:bg-stone-100 whitespace-nowrap"
                                  title={`Tarik dari PO: ${formatIDR(suggested)}`}
                                  data-testid={`preview-pull-price-${i}`}
                                >PO</button>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-3 text-right font-mono-num">{formatIDR(st)}</td>
                          <td className="py-2 px-2 text-right">
                            <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-stone-400 hover:text-red-700" title="Hapus item" data-testid={`preview-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Button variant="secondary" className="!py-1.5 !px-3 text-xs" onClick={() => setItems([...items, { material_name: "", unit: "", quantity: 0, unit_price: 0 }])} data-testid="preview-add-item-btn">
                  <Plus className="h-3.5 w-3.5" />Tambah Item
                </Button>
                <Button variant="secondary" className="!py-1.5 !px-3 text-xs" onClick={() => {
                  const v = items.map((it) => ({ ...it, unit_price: lookupPoPrice(it.material_name, it.unit) || it.unit_price }));
                  setItems(v);
                  toast.success("Harga ditarik dari PO/Daftar Harga");
                }} data-testid="preview-pull-all-btn">
                  <FileText className="h-3.5 w-3.5" />Tarik Semua Harga dari PO
                </Button>
              </div>
              <div className="border-t border-stone-200 pt-4 mt-2">
                <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold mb-2">Faktur Pajak</p>
                <Field label="Unggah Faktur Pajak (PDF/JPG/PNG)" hint="Akan digabungkan ke PDF final.">
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 cursor-pointer hover:bg-stone-50 text-sm">
                      <Upload className="h-4 w-4" />{fakturUploading ? "Mengunggah…" : "Pilih file"}
                      <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={uploadFaktur} className="hidden" data-testid="preview-faktur-input" />
                    </label>
                    {cfg.faktur_file_path && (
                      <>
                        <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                          <FileText className="h-3 w-3" /> {cfg.faktur_file_name}
                        </span>
                        <button onClick={() => setPreviewId({ id: cfg.faktur_file_id, name: cfg.faktur_file_name, ct: cfg.faktur_file_content_type })} className="text-terra-600 hover:text-terra-700 inline-flex items-center gap-1 text-xs" type="button">
                          <Eye className="h-3.5 w-3.5" />Preview
                        </button>
                        <button onClick={() => setCfg({ ...cfg, faktur_file_path: "", faktur_file_id: "", faktur_file_name: "", faktur_file_content_type: "" })} className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-xs" type="button">
                          <Trash2 className="h-3.5 w-3.5" />Hapus
                        </button>
                      </>
                    )}
                  </div>
                </Field>
              </div>
            </div>
            <div className="p-6 bg-stone-50 border-l border-stone-200 space-y-4 text-sm">
              <div className="space-y-1 pb-3 border-b border-stone-200" data-testid="preview-meta">
                <h3 className="font-display text-xs uppercase tracking-widest text-stone-500 mb-2">Detail Tagihan</h3>
                <Row k="No Invoice" v={<span className="font-mono">{cfg.invoice_number || <i className="text-stone-400">otomatis</i>}</span>} />
                {/* Iter v34cb (#1) — Tampilkan Tanggal Invoice yang di-input di konfigurasi */}
                <Row k="Tanggal Invoice" v={<span className="font-mono">{cfg.invoice_date ? formatDate(cfg.invoice_date) : <i className="text-stone-400">—</i>}</span>} />
                <Row k="No PO" v={<span className="font-mono">{pos.find(p=>p.id===cfg.po_id)?.po_number || <i className="text-stone-400">—</i>}</span>} />
                <Row k="Klien" v={<span>{cfg.client_name || <i className="text-stone-400">—</i>}</span>} />
                {cfg.use_vat && (() => {
                  const cl = clients.find(c => c.name === cfg.client_name);
                  return <Row k="NPWP" v={<span className="font-mono">{cl?.npwp || <i className="text-stone-400">—</i>}</span>} />;
                })()}
              </div>
              <h3 className="font-display text-base font-semibold">Ringkasan</h3>
              <Row k="Subtotal" v={formatIDR(subtotal)} />
              {!cfg.is_dp_invoice && cfg.use_discount && <Row k={t("discount")} v={`- ${formatIDR(discount)}`} />}
              {!cfg.is_dp_invoice && cfg.use_vat && cfg.use_dpp_nilai_lain && <Row k="DPP Nilai Lain" v={formatIDR(dpp)} />}
              {!cfg.is_dp_invoice && cfg.use_vat && <Row k={`PPN ${cfg.vat_rate}%`} v={`+ ${formatIDR(vat)}`} />}
              {!cfg.is_dp_invoice && cfg.use_pph && <Row k={`${t("pph")} (${cfg.pph_rate}%)`} v={`- ${formatIDR(pph)}`} />}
              {!cfg.is_dp_invoice && cfg.use_dp && <Row k={t("down_payment")} v={`- ${formatIDR(dp)}`} />}
              {!cfg.is_dp_invoice && cfg.use_dp && dpVatDeduct > 0 && <Row k="PPN Uang Muka" v={`- ${formatIDR(dpVatDeduct)}`} />}
              {/* Iter v34{ah} — DP invoice bills only the Uang Muka amount. */}
              {cfg.is_dp_invoice && (
                <Row
                  k={cfg.dp_invoice_mode === "percentage" && cfg.dp_invoice_percentage
                    ? `Uang Muka (${cfg.dp_invoice_percentage}%)`
                    : "Uang Muka"}
                  v={formatIDR(dpInvoiceAmt)}
                />
              )}
              {/* Iter v34cz (#2) — PPN uang muka ala Coretax */}
              {cfg.is_dp_invoice && cfg.use_vat && cfg.use_dpp_nilai_lain && <Row k="DPP Nilai Lain" v={formatIDR(dpInvDpp)} />}
              {cfg.is_dp_invoice && cfg.use_vat && <Row k={`PPN ${cfg.vat_rate}%`} v={`+ ${formatIDR(dpInvVat)}`} />}
              <div className="border-t border-stone-300 pt-2 mt-2">
                <Row k={<strong>{t("grand_total")}</strong>} v={<strong className="font-mono-num text-base">{formatIDR(grand)}</strong>} />
              </div>
            </div>
          </div>
          <div className="flex justify-between p-6 border-t border-stone-200">
            <Button variant="ghost" onClick={back}>← {t("back")}</Button>
            <Button onClick={finish} disabled={finishing} data-testid="stage-finish">
              {finishing ? "Menyimpan…" : `${t("finish")} →`}
            </Button>
          </div>
        </Card>
      )}

      {stage === 3 && createdInvoice && (
        <Card className="p-8 text-center space-y-4">
          <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
          <h2 className="font-display text-2xl font-bold">{editingId ? "Invoice diperbarui" : "Invoice dibuat"}</h2>
          <p className="font-mono text-sm text-stone-600">No: {createdInvoice.invoice_number}</p>
          <p className="font-mono-num text-3xl text-stone-900">{formatIDR(createdInvoice.grand_total)}</p>
          <div className="flex items-center justify-center gap-3 pt-4 flex-wrap">
            <Button variant="secondary" onClick={printInvoice} data-testid="print-invoice-btn"><Printer className="h-4 w-4" />{t("print")} PDF</Button>
            <Button variant="secondary" onClick={downloadInvoicePdf} data-testid="download-invoice-pdf-btn"><Download className="h-4 w-4" />Download PDF</Button>
            <Button onClick={reset} data-testid="new-invoice-btn">Buat invoice baru</Button>
          </div>
        </Card>
      )}
      <FilePreview open={!!previewId} onClose={() => setPreviewId(null)} fileId={previewId?.id} filename={previewId?.name} contentType={previewId?.ct} />

      {/* SJ Picker Modal — Tarik Surat Jalan */}
      <SjPickerModal
        open={sjPickerOpen}
        onClose={() => setSjPickerOpen(false)}
        cfg={cfg}
        pos={pos}
        source={sjPickerSource}
        selection={sjPickerSelection}
        setSelection={setSjPickerSelection}
        filterPo={sjPickerFilterPo}
        setFilterPo={setSjPickerFilterPo}
        onConfirm={confirmPickedSjs}
      />

      {/* Manual SJ Modal — Input SJ Manual */}
      <ManualSjModal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        manualForm={manualForm}
        setManualForm={setManualForm}
        manualEditIdx={manualEditIdx}
        sjCodes={sjCodes}
        onSave={saveManual}
      />
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex items-center justify-between"><span className="text-stone-600">{k}</span><span className="font-mono-num">{v}</span></div>;
}
